import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { CreateChargesDialog, EquipmentChargesDialog } from 'modules/equipment';
import { Skeleton, Stack } from '@mui/material';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import { ModalsKeys, Nullable } from 'global';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { CalculatedTotalsByLevel, useQuote } from 'modules/quote';
import { mapEquipmentSourceToItem } from '../lib';
import {
  DealBuilderCreditApplicationSource,
  DealBuilderEquipmentSource,
  DealBuilderFormValues,
  DealBuilderStep,
} from '../types';
import { dealBuilderApi, InventoryEquipmentItem } from '../api';
import { useDealBuilderPersistence, useDealBuilderStepNavigation } from '../hooks';
import { useNotifications } from 'modules/notification';
import { DealBuilderContextProvider, DeliverySubmitRef } from '../context/DealBuilderContext';
import { BuildAQuoteCard } from './components/BuildAQuoteCard';
import { QuoteHistory } from './components/QuoteHistory';
import { ContactsStep } from './steps/contacts';
import { EquipmentStep } from './steps/equipment';
import { PricingStep } from './steps/pricing';
import { FinanceMenuStep } from './steps/financeMenu';
import { CreateUpdateEquipmentDialog, UploadImagesForNewEquipmentDialog } from 'modules/equipment';
import { ModalsContainer } from 'components/shared/Modals';
import { QuoteDepositType } from '@trnsact/trnsact-shared-types';
import { ReviewAndSendStep } from './steps/reviewAndSend';
import { quoteAPI } from '../../quote/api';

const STEP_COMPONENTS: Record<DealBuilderStep, React.ComponentType> = {
  [DealBuilderStep.Contacts]: ContactsStep,
  [DealBuilderStep.Equipment]: EquipmentStep,
  [DealBuilderStep.Pricing]: PricingStep,
  [DealBuilderStep.FinanceMenu]: FinanceMenuStep,
  [DealBuilderStep.ReviewSend]: ReviewAndSendStep,
};

interface Props {
  vo: VendorOpportunity;
  creditApplication: DealBuilderCreditApplicationSource;
  equipmentData: Nullable<DealBuilderEquipmentSource[]>;
  activeProposalId: Nullable<string>;
  onRemount: (proposalId?: string) => void;
}

const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
  [ModalsKeys.CreateUpdateEquipmentDialog]: <CreateUpdateEquipmentDialog />,
  [ModalsKeys.UploadImagesForNewEquipmentDialog]: <UploadImagesForNewEquipmentDialog />,
  [ModalsKeys.CreateChargesEquipmentDialog]: <CreateChargesDialog />,
  [ModalsKeys.ChargesEquipmentDialog]: <EquipmentChargesDialog />,
};

export const DealBuilderWrapper = ({ vo, creditApplication, equipmentData, activeProposalId, onRemount }: Props) => {
  const { setValue } = useFormContext<DealBuilderFormValues>();
  const { viewStep } = useDealBuilderStepNavigation();
  const { showNotification } = useNotifications();
  const [startingNewQuote, setStartingNewQuote] = useState(false);
  const [selectedEquipmentItem, setSelectedEquipmentItem] = useState<InventoryEquipmentItem | null>(null);
  const [calculatedTotals, setCalculatedTotals] = useState<CalculatedTotalsByLevel>({
    invoice: { fees: 0, taxes: 0, price: 0 },
    equipments: { fees: 0, taxes: 0, price: 0 },
    attachments: { fees: 0, taxes: 0, price: 0 },
  });
  const { isQuoteCalcLoading } = useQuote();
  const deliveryAddressSubmitRef = useRef<Nullable<{ submit: () => Promise<{ isValid: boolean; formValues: any }> }>>(
    null
  ) as DeliverySubmitRef;

  const [fetchEquipmentById] = useLazyQuery<{ equipment: DealBuilderEquipmentSource }, { id: string }>(
    dealBuilderApi.getEquipmentDetailsById
  );

  const {
    activeEntity,
    historyEntities,
    contactsData,
    loading: dealBuilderLoading,
    initialData,
    startNewQuote: startNewQuoteRaw,
    setActiveEntity,
  } = useDealBuilderPersistence(vo, activeProposalId, creditApplication?.id ?? null);

  const activeQuoteId = activeEntity?.quote?.quoteId;

  const { data } = useQuery(quoteAPI.getQuoteCalcById, {
    skip: !activeQuoteId,
    variables: {
      quoteId: activeQuoteId,
    },
    onCompleted: console.log,
  });

  const priceCalc = data?.quoteCalc?.byQuoteId?.entity ?? null;

  useEffect(() => {
    if (!initialData || dealBuilderLoading) return;

    //Auto-populate Contacts
    setValue('contacts.ownerPgIds', initialData.ownerPgIds ?? []);
    setValue('contacts.accountContactIds', initialData.accountContactIds ?? []);

    //Auto-populate Equipment
    if (initialData.equipmentId) {
      setValue('equipment.selectedId', initialData.equipmentId);
      const source = equipmentData?.find(e => e.equipmentId === initialData.equipmentId);
      if (source) {
        setSelectedEquipmentItem(mapEquipmentSourceToItem(source));
      } else {
        fetchEquipmentById({ variables: { id: initialData.equipmentId } }).then(({ data }) => {
          if (data?.equipment) setSelectedEquipmentItem(mapEquipmentSourceToItem(data.equipment));
        });
      }
    }

    //Auto-populate Pricing
    if (initialData.quote?.deposit) {
      const { type, value, notes } = initialData.quote.deposit;
      setValue('pricing.customerDeposit.type', type as QuoteDepositType);
      setValue('pricing.customerDeposit.value', value as any);
      setValue('pricing.customerDeposit.notes', notes ?? '');
    }

    //Auto-populate Trade-In
    if (initialData.quote?.equipmentTradeIns?.length) {
      Promise.all(
        initialData.quote.equipmentTradeIns.map(async tradeIn => {
          const { data } = await fetchEquipmentById({ variables: { id: tradeIn.equipmentId } });
          const equipment = data?.equipment;
          return {
            equipmentId: tradeIn.equipmentId,
            make: equipment?.make ?? null,
            model: equipment?.model ?? null,
            year: equipment?.year ?? null,
            newUsed: equipment?.newUsed ?? null,
            serialNumberVin: equipment?.serialNumberVin ?? tradeIn.vin ?? null,
            mileage: equipment?.mileage ?? null,
            tradeInValue: tradeIn.tradeInDetails.tradeValue,
            amountOwed: tradeIn.tradeInDetails.tradeAmountOwed,
            otherCharges: tradeIn.tradeInDetails.tradeOtherCharges ?? null,
            netTrade: null,
          };
        })
      ).then(rows => setValue('pricing.tradeIn', rows));
    }

    //Auto-populate Finance&Menu
    setValue('financeMenu.isEnabled', !!initialData.proposal?.settings?.configuration?.formData);
    const savedSaleType = initialData.proposal?.settings?.configuration?.formData?.saleDetails?.saleType;
    if (savedSaleType) {
      setValue('financeMenu.saleType', savedSaleType);
    }

    setCalculatedTotals(initialData.calculatedTotals);
  }, [initialData, dealBuilderLoading, equipmentData, fetchEquipmentById, setValue]);

  const handleSetSelectedEquipmentItem = useCallback((item: InventoryEquipmentItem | null) => {
    setSelectedEquipmentItem(item);
  }, []);

  const startNewQuote = useCallback(async () => {
    setStartingNewQuote(true);
    const id = await startNewQuoteRaw();
    if (id) {
      showNotification('New quote created successfully');
      onRemount(id);
    }
    setStartingNewQuote(false);
    return id;
  }, [startNewQuoteRaw, showNotification, onRemount]);

  const selectSavedProposal = useCallback(
    (proposalId: string) => {
      onRemount(proposalId);
    },
    [onRemount]
  );

  const StepComponent = STEP_COMPONENTS[viewStep];

  const isInitialLoading = dealBuilderLoading || startingNewQuote;

  return (
    <DealBuilderContextProvider
      value={{
        activeEntity,
        setActiveEntity,
        startNewQuote,
        sourceData: {
          vo,
          equipments: equipmentData,
          creditApplication,
        },
        contacts: {
          contactsData,
        },
        equipment: {
          deliveryAddressSubmitRef,
          selectedEquipmentItem,
          setSelectedEquipmentItem: handleSetSelectedEquipmentItem,
        },
        pricing: {
          calculatedTotals,
          setCalculatedTotals,
          priceCalc,
          isQuoteCalcLoading,
        },
      }}
    >
      {isInitialLoading ? (
        <Stack gap={2}>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        </Stack>
      ) : (
        <Stack gap={2}>
          <BuildAQuoteCard />
          <StepComponent />
          <QuoteHistory historyEntities={historyEntities} selectSavedProposal={selectSavedProposal} />
        </Stack>
      )}

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </DealBuilderContextProvider>
  );
};
